import {
  Directive,
  ElementRef,
  Renderer2,
  inject,
  input,
  effect,
  afterNextRender,
  Injector,
  untracked,
  DestroyRef,
  DOCUMENT, EffectCleanupRegisterFn
} from '@angular/core';
import { isObservable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InteractivityService } from '../../services/interactivity-service';
import type { Observable } from 'rxjs';
import type { InputSignal } from '@angular/core';

export enum FocusPosition {
  TOP = 'top'
}

@Directive({
  selector: '[tdWbFocus]',
  exportAs: 'tdWbFocus'
})
export class FocusDirective {

  readonly focus: InputSignal<boolean | Observable<unknown>> = input<boolean | Observable<unknown>>(false, {
    alias: 'tdWbFocus'
  });

  readonly focusPosition: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'tdWbFocusPosition' });
  /**
   * {boolean} default false, i.e., focus on the current element even if it's not tabbable.
   * If true, focus on the current element if it's tabbable, otherwise focus on first tabbable children.
   */
  readonly tdWbFocusCheckTabbable: InputSignal<boolean> = input<boolean>(false);
  readonly tdWbFocusDelay: InputSignal<number | undefined> = input<number | undefined>(undefined);

  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly interactivityService: InteractivityService = inject(InteractivityService);
  private readonly injector: Injector = inject(Injector);
  private readonly document: Document = inject(DOCUMENT);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      if (this.focusPosition() === FocusPosition.TOP) {
        untracked(() => {
          this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'absolute');
          this.renderer.setStyle(this.elementRef.nativeElement, 'top', '0');
          this.renderer.setStyle(this.elementRef.nativeElement, 'left', '0');
        });
      }
    });

    effect((onCleanup: EffectCleanupRegisterFn) => {
      const focusVal = this.focus();

      if (isObservable(focusVal)) {
        const subscription = focusVal.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {
          this.doFocus();
        });
        onCleanup(() => subscription.unsubscribe());
      } else if (focusVal) {
        untracked(() => this.doFocus());
      }
    });
  }

  private doFocus(): void {
    afterNextRender(() => {
      const delay = this.tdWbFocusDelay();
      if (delay === undefined) {
        this.doFocusNow();
      } else {
        setTimeout(() => {
          this.doFocusNow();
        }, delay);
      }
    }, { injector: this.injector });
  }

  private doFocusNow(): void {
    const hostElement = this.elementRef.nativeElement;
    if (!hostElement) {
      return;
    }
    const elementRef = this.tdWbFocusCheckTabbable() ?
      this.interactivityService.getFirstTabbableElement(hostElement) :
      hostElement;
    if (elementRef && typeof elementRef.focus === 'function' && elementRef !== this.document.activeElement) {
      elementRef.focus();
    }
  }
}
